import { createElement, memo, useEffect } from '@max/max';
import { useMonitorEngineContext } from '@nibfe/mcp-monitor-sdk';
import { useIntentionData } from './hooks/useIntentionData';
import { usePersonaCrowd } from './hooks/usePersonaCrowd';
import { RouterScene } from './types/shared';
import View from '@hfe/max-view';
/**
 * 意向度流量路由组件
 *
 * 职责：
 * 1. 请求用户意向度接口，获取各业务线意向度分数
 * 2. 根据路由场景和人群定向，决定最终 Tab 排序/切换
 * 3. 人群定向命中时通过 TabReorderEvent 通知 Tab 整体重排顺序
 * 4. 业务意向命中时通过 TabFollowEvent 切换 Tab 到对应 index
 *
 * 本组件不渲染任何 UI，仅作为逻辑驱动层。
 */
const IntentedTrafficRouter = (props) => {
    const { tabBusinessLines = [], routerScene = RouterScene.BizDispatch, personaTabOrders = [] } = props;
    const ctx = useMonitorEngineContext();
    const { queryIntentionInfo, maxIntentionBiz } = useIntentionData();
    const { checkPersonaHit } = usePersonaCrowd();
    // 通过 SortTabsEvent 通知 Tab 整体重排顺序
    const emitReorder = (list) => {
        console.log('SortTabsEvent emit', list);
        ctx._communication?.emit('SortTabsEvent', {
            list
        });
    };
    // 通过 TabFollowEvent 切换 Tab 到指定 index
    const emitFollow = (index) => {
        console.log('TabFollowEvent emit', index);
        ctx._communication?.emit('TabFollowEvent', {
            index
        });
    };
    const personaIds = personaTabOrders.map(item => item.personaId);
    // 默认 Tab 顺序：[0, 1, 2, ...]，人群接口异常时兜底使用
    const defaultTabOrder = tabBusinessLines.map((_, i) => i);
    useEffect(() => {
        const run = async () => {
            // 并行请求：业务意向 + 人群定向
            // checkPersonaHit 失败时返回空 Map，personaFailed 标记接口是否异常
            let personaFailed = false;
            const [, hitMap] = await Promise.all([queryIntentionInfo().catch(() => { }), personaIds.length > 0 ? checkPersonaHit(personaIds).then(map => {
                    // 接口返回 null 视为失败
                    if (map.size === 0 && personaIds.length > 0)
                        personaFailed = true;
                    return map;
                }).catch(() => {
                    personaFailed = true;
                    return new Map();
                }) : Promise.resolve(new Map())]);
            // 找到第一个命中的人群定向配置（personaTabOrders 按优先级从高到低排列）
            const hitPersonaConfig = personaTabOrders.find(item => hitMap.get(item.personaId) === true);
            // 解析人群定向的完整 Tab 排序数组；接口异常时返回默认顺序兜底
            const getPersonaOrder = () => {
                if (personaFailed || !hitPersonaConfig) {
                    // 接口失败或未命中任何人群，使用默认顺序兜底
                    return {
                        list: defaultTabOrder,
                        isFallback: true
                    };
                }
                const parsed = hitPersonaConfig.tabOrder.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !Number.isNaN(n));
                if (parsed.length === 0) {
                    return {
                        list: defaultTabOrder,
                        isFallback: true
                    };
                }
                return {
                    list: parsed,
                    isFallback: false
                };
            };
            // 根据业务意向找到目标 index
            const getBizTargetIndex = () => {
                if (!maxIntentionBiz.current)
                    return -1;
                return tabBusinessLines.findIndex(line => line.bizType === maxIntentionBiz.current);
            };
            // SortTabsEvent 无论何种情况都必须触发，Tab 组件强依赖此事件渲染
            const { list: personaList, isFallback } = getPersonaOrder();
            if (routerScene === RouterScene.PersonaCrowd) {
                // 人群场景：人群定向 > 业务意向 > 运营配置默认顺序
                if (!isFallback) {
                    // 命中人群：按人群配置顺序重排
                    emitReorder(personaList);
                }
                else {
                    // 无论业务意向是否命中，都发送默认顺序兜底
                    emitReorder(personaList);
                    // 未命中人群/接口异常：先尝试业务意向切换，再兜底默认顺序重排
                    const bizIndex = getBizTargetIndex();
                    if (bizIndex !== -1) {
                        emitFollow(bizIndex);
                    }
                }
            }
            else {
                // 无论业务意向是否命中，都发送重排事件（命中人群用配置顺序，否则默认顺序）
                emitReorder(defaultTabOrder);
                // 业务分发场景：业务意向
                const bizIndex = getBizTargetIndex();
                if (bizIndex !== -1) {
                    // 命中业务意向：切换 Tab
                    emitFollow(bizIndex);
                }
            }
        };
        run();
    }, []);
    return <View></View>;
};
export default memo(IntentedTrafficRouter);
